<template>
  <div>
    <!-- 顶部Banner -->
    <div class="top-banner">
      <img src="@/assets/topbanner.jpg" alt="华洲电力" class="banner-img" />
    </div>
    
    <!-- 联系我们主体内容 -->
    <div class="contact-container">
      <div class="contact-content">
        <!-- 标题区域 -->
        <div class="title-section">
          <h1>联系我们</h1>
          <div class="title-line"></div>
        </div>
        
        <!-- 地图和联系信息区域 -->
        <div class="contact-main">
          <!-- 左侧地图 -->
          <div class="map-container">
            <div class="map-placeholder">
              <!-- 地图占位符 -->
              <div class="map-image">
                <baidu-map
                ref="baiduMapRef"
                class="map-container"
                :center="mapCenter"
                :zoom="15"
                :scroll-wheel-zoom="true"
                v-if="isMounted && mapCenter.lng && mapCenter.lat"
                @ready="onMapReady"
              >
                <!-- 添加标记点 -->
                <bm-marker
                  :position="mapCenter"
                  :dragging="false"
                >
                  <!-- 信息窗口 -->
                  <bm-info-window
                    :title="'华洲电力建设有限公司'"
                    :content="aboutInfo.contactInfo.address"
                    :show="true"
                  />
                </bm-marker>
                <!-- 添加导航控件 -->
                <bm-navigation
                  anchor="BMAP_ANCHOR_TOP_LEFT"
                />
                <!-- 添加比例尺控件 -->
                <bm-scale
                  anchor="BMAP_ANCHOR_BOTTOM_RIGHT"
                />
              </baidu-map> 
              </div>
            </div>
          </div>
          
          <!-- 右侧联系信息 -->
          <div class="contact-info">
            <div class="info-item">
              <div class="info-icon location-icon"><el-icon :size="24"><LocationFilled /></el-icon></div>
              <div class="info-content">
                <h3>地址</h3>
                <p>湖南省湘乡市湘乡经济开发区大将北路30号</p>
              </div>
            </div>
            
            <div class="info-item">
              <div class="info-icon phone-icon"><el-icon :size="24"><PhoneFilled /></el-icon></div>
              <div class="info-content">
                <h3>电话</h3>
                <p>0731-56780003</p>
              </div>
            </div>
            
            <div class="info-item">
              <div class="info-icon email-icon"><el-icon :size="24"><Comment /></el-icon></div>
              <div class="info-content">
                <h3>邮箱</h3>
                <p>huazhoudianli@163.com</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>    
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, onBeforeUnmount, computed } from 'vue'
import axios from 'axios'
import { ElMessage } from 'element-plus'
import { PhoneFilled } from '@element-plus/icons-vue'
import { Comment } from '@element-plus/icons-vue'
import { LocationFilled } from '@element-plus/icons-vue'
import { useHead } from '@vueuse/head'

// SEO Meta标签配置
useHead({
  title: '联系我们 - 华洲电力建设有限公司',
  meta: [
    {
      name: 'description',
      content: '华洲电力建设有限公司联系方式，地址：湖南省湘乡市湘乡经济开发区大将北路30号，电话：0731-56780003，邮箱：huazhoudianli@163.com。'
    },
    {
      name: 'keywords',
      content: '华洲电力,联系方式,电力工程公司,联系电话,公司地址'
    },
    {
      name: 'author',
      content: '华洲电力建设有限公司'
    },
    {
      property: 'og:title',
      content: '联系我们 - 华洲电力建设有限公司'
    },
    {
      property: 'og:description',
      content: '华洲电力建设有限公司联系方式，地址：湖南省湘乡市湘乡经济开发区大将北路30号，电话：0731-56780003，邮箱：huazhoudianli@163.com。'
    },
    {
      property: 'og:type',
      content: 'website'
    }
  ]
})



const isMounted = ref(false)
const baiduMapRef = ref(null)
const mapInstance = ref(null)

const aboutInfo = reactive({
  companyIntro: '',
  qualifications: '',
  developmentVision: '',
  enterpriseSpirit: '',
  businessScope: '',
  technicalTeam: '',
  mainBusiness: [],
  coreValues: {
    honesty: '',
    performance: '',
    people: ''
  },
  contactInfo: {
    address: '湖南省湘乡市湘乡经济开发区大将北路30号',
    contactPerson: '',
    email: 'huazhoudianli@163.com',
    phone: '0731-56780003'
  },
  location: { lng: 112.5398, lat: 27.7731 } // 默认坐标
})

// 使用计算属性确保地图中心坐标始终有效
const mapCenter = computed(() => ({
  lng: aboutInfo.location?.lng || 112.5398,
  lat: aboutInfo.location?.lat || 27.7731
}))

onMounted(async () => {
  isMounted.value = true
  try {
    const res = await axios.get('/webapi/about/getAboutInfo')
    if (res.data.ActionType === 'OK' && res.data.data) {
      Object.assign(aboutInfo, res.data.data)
    } else {
      // 如果获取失败，使用默认数据
      console.log('使用默认联系信息')
    }
  } catch (error) {
    console.error('获取公司信息失败:', error)
    // 使用默认数据作为备用
  }
})

// 地图准备就绪时保存实例引用
function onMapReady(map) {
  if (isMounted.value) {
    mapInstance.value = map
  }
}

// 清理地图资源
function cleanupMapResources() {
  if (mapInstance.value) {
    try {
      // 尝试清理地图实例
      mapInstance.value = null
    } catch (error) {
      console.error('清理地图资源失败:', error)
    }
  }
  
  // 尝试清理百度地图可能留下的全局资源
  try {
    // 这部分代码取决于百度地图API的具体实现
    if (window.BMap) {
      delete window.BMap
    }
  } catch (error) {
    console.error('清理百度地图全局资源失败:', error)
  }
}

onBeforeUnmount(() => {
  isMounted.value = false
  cleanupMapResources()
})
</script>

<style scoped>
/* 顶部Banner样式 */
.top-banner {
  width: 100%;
  height: auto;
  overflow: hidden;
}

.banner-img {
  width: 100%;
  height: auto;
  display: block;
}

/* 联系我们容器 */
.contact-container {
  padding: 50px 0;
  background-color: #ffffff;
}

.contact-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

/* 标题样式 */
.title-section {
  text-align: center;
  margin-bottom: 40px;
}

.title-section h1 {
  font-size: 2.2rem;
  color: #333333;
  margin-bottom: 15px;
  font-weight: 300;
}

.title-line {
  width: 60px;
  height: 2px;
  background-color: #3f5ff0;
  margin: 0 auto;
}

/* 主体内容区域 */
.contact-main {
  display: flex;
  gap: 30px;
  margin-top: 30px;
}

/* 地图容器 */
.map-container {
  flex: 1;
  height: 400px;
  border-radius: 4px;
  overflow: hidden;
  background-color: #f5f5f5;
  position: relative;
}

.map-placeholder {
  width: 100%;
  height: 100%;
}

.map-image {
  width: 100%;
  height: 100%;
  background-color: #e8f4ff;
  background-size: cover;
  background-position: center;
}

/* 联系信息 */
.contact-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 25px;
}

.info-item {
  display: flex;
  align-items: flex-start;
  gap: 15px;
  padding: 20px;
  background-color: #f9f9f9;
  border-radius: 4px;
  transition: all 0.3s ease;
}

.info-icon {
  font-size: 24px;
  color: #3f5ff0;
  flex-shrink: 0;
}

.info-content h3 {
  font-size: 1.1rem;
  color: #3f5ff0;
  margin: 0 0 8px 0;
  font-weight: 500;
}

.info-content p {
  font-size: 1rem;
  color: #666666;
  margin: 0;
  line-height: 1.5;
}

.contact-detail {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 12px;
  font-size: 0.9rem;
  color: #cccccc;
  line-height: 1.5;
}

.contact-icon {
  color: #3f5ff0;
  font-size: 16px;
  flex-shrink: 0;
  margin-top: 2px;
}

/* 响应式设计 */
@media (max-width: 992px) {
  .contact-main {
    flex-direction: column;
  }
  
  .map-container {
    height: 300px;
  }
}

@media (max-width: 768px) {
  .contact-container {
    padding: 40px 0;
  }
  
  .title-section h1 {
    font-size: 1.8rem;
  }
  
  .info-item {
    padding: 15px;
  }
}
</style>